import styles from "./ReorderDialog.module.scss";
import { AppDialog } from "../AppDialog/AppDialog";
import { DefaultButton } from "../button/defaultButton/DefaultButton";
import { useState, useCallback, useEffect, ReactNode } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "./SortableItem";
import { Icon, IconType } from "../../icons/Icon";

export interface IReorderableItem {
  id: number;
  name?: string;
}

interface IReorderDialogProps<T extends IReorderableItem> {
  visible: boolean;
  onHide: () => void;
  title: string;
  items: T[];
  onSave: (orderedIds: number[]) => Promise<void>;
  loading?: boolean;
  itemTemplate?: (item: T) => ReactNode;
}

export function ReorderDialog<T extends IReorderableItem>({
  visible,
  onHide,
  title,
  items,
  onSave,
  loading = false,
  itemTemplate,
}: IReorderDialogProps<T>) {
  const [orderedItems, setOrderedItems] = useState<T[]>(items);
  const [isSaving, setIsSaving] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);

  // Update ordered items when items prop changes
  useEffect(() => {
    setOrderedItems(items);
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setOrderedItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const orderedIds = orderedItems.map((item) => item.id);
      await onSave(orderedIds);
      onHide();
    } finally {
      setIsSaving(false);
    }
  };

  const handleHide = () => {
    setOrderedItems(items);
    onHide();
  };

  const activeItem = activeId
    ? orderedItems.find((item) => item.id === activeId)
    : null;

  return (
    <AppDialog
      visible={visible}
      onHide={handleHide}
      header={title}
      className={styles.dialog}
    >
      <div className={styles.content}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={orderedItems.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className={styles.list}>
              {orderedItems.map((item) => (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  isDragOverlay={false}
                  isBeingDragged={activeId === item.id}
                >
                  {itemTemplate ? (
                    itemTemplate(item)
                  ) : (
                    <span className={styles.item_name}>{item.name}</span>
                  )}
                </SortableItem>
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeItem ? (
              <div className={styles.drag_overlay_item}>
                <div className={styles.item_handle}>
                  <Icon
                    type={IconType.DragHandle}
                    width={20}
                    height={20}
                    color="rgba(255, 255, 255, 0.7)"
                  />
                </div>
                <div className={styles.item_content}>
                  {itemTemplate ? (
                    itemTemplate(activeItem)
                  ) : (
                    <span className={styles.item_name}>{activeItem.name}</span>
                  )}
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        <div className={styles.actions}>
          <DefaultButton
            label="Отказ"
            design="outline"
            onClick={handleHide}
            disabled={isSaving}
          />
          <DefaultButton
            label="Запази"
            onClick={handleSave}
            loading={isSaving || loading}
            disabled={isSaving || loading}
          />
        </div>
      </div>
    </AppDialog>
  );
}
